import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle, Globe, Check, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import AnimatedSection from '../components/AnimatedSection';
import { pageTranslations, useLanguage } from '../i18n/pageTranslations';

const Contacto = () => {
  const lang = useLanguage();
  const t = pageTranslations.contacto[lang] || pageTranslations.contacto.es;

  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    pais: '',
    ciudad: '',
    procedimiento: '',
    referidoPor: '',
    medioPreferido: '',
    horarioContacto: '',
    mensaje: '',
    privacidad: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const procedimientos = {
    es: [
      '— Cirugía Facial —', 'Blefaroplastia', 'Lifting Facial y Cervical', 'Rinoplastia', 'Otoplastia', 'Ojeras y Bolsas',
      '— Cirugía Corporal —', 'Cirugía de Mamas', 'Ginecomastia', 'Abdominoplastia', 'Cirugía Postbariátrica', 'Liposucción',
      '— Medicina Estética —', 'Rinomodelación®', 'Endolifting Láser', 'Eliminación de Papada', 'Armonización Facial', 'Ojeras sin Cirugía',
      '— Cirugía Reparadora —', 'Cirugía Dermatológica', 'Cirugía Plástica Secundaria', 'Segunda Opinión', 'Peritación Médica',
      '— Otro —', 'Consulta general',
    ],
    en: [
      '— Facial Surgery —', 'Blepharoplasty', 'Facelift', 'Rhinoplasty', 'Otoplasty', 'Dark Circles and Bags',
      '— Body Surgery —', 'Breast Surgery', 'Gynecomastia', 'Abdominoplasty', 'Post-Bariatric Surgery', 'Liposuction',
      '— Aesthetic Medicine —', 'Rhinomodeling®', 'Laser Endolifting', 'Jowl Removal', 'Facial Harmonization', 'Non-Surgical Dark Circles',
      '— Reconstructive Surgery —', 'Dermatological Surgery', 'Secondary Plastic Surgery', 'Second Opinion', 'Medical Expertise',
      '— Other —', 'General inquiry',
    ],
    fr: [
      '— Chirurgie Faciale —', 'Blépharoplastie', 'Lifting Facial', 'Rhinoplastie', 'Otoplastie', 'Cernes et Poches',
      '— Chirurgie Corporelle —', 'Chirurgie Mammaire', 'Gynécomastie', 'Abdominoplastie', 'Chirurgie Post-Bariatrique', 'Liposuccion',
      '— Médecine Esthétique —', 'Rhinomodelage®', 'Endolifting Laser', 'Double Menton', 'Harmonisation Faciale', 'Cernes sans Chirurgie',
      '— Chirurgie Réparatrice —', 'Chirurgie Dermatologique', 'Chirurgie Plastique Secondaire', 'Deuxième Avis', 'Expertise Médicale',
      '— Autre —', 'Demande générale',
    ],
    pt: [
      '— Cirurgia Facial —', 'Blefaroplastia', 'Lifting Facial', 'Rinoplastia', 'Otoplastia', 'Olheiras e Bolsas',
      '— Cirurgia Corporal —', 'Cirurgia de Mamas', 'Ginecomastia', 'Abdominoplastia', 'Cirurgia Pós-Bariátrica', 'Lipoaspiração',
      '— Medicina Estética —', 'Rinomodelação®', 'Endolifting Laser', 'Eliminação de Papada', 'Harmonização Facial', 'Olheiras sem Cirurgia',
      '— Cirurgia Reparadora —', 'Cirurgia Dermatológica', 'Cirurgia Plástica Secundária', 'Segunda Opinião', 'Perícia Médica',
      '— Outro —', 'Consulta geral',
    ],
  };

  const currentProcedimientos = procedimientos[lang] || procedimientos.es;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacto/general`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre: formData.nombre,
          email: formData.email,
          telefono: formData.telefono,
          pais: formData.pais,
          ciudad: formData.ciudad,
          procedimiento: formData.procedimiento,
          referido_por: formData.referidoPor,
          medio_preferido: formData.medioPreferido,
          horario_contacto: formData.horarioContacto,
          mensaje: formData.mensaje
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '', email: '', telefono: '', pais: '', ciudad: '',
          procedimiento: '', referidoPor: '', medioPreferido: '', horarioContacto: '', mensaje: '', privacidad: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <div className="min-h-screen bg-cream">
        <Header />
        <section className="py-32">
          <div className="container mx-auto px-6 max-w-2xl text-center">
            <div className="w-20 h-20 border border-gold flex items-center justify-center mx-auto mb-8">
              <Check className="text-gold" size={40} />
            </div>
            <h1 className="font-serif text-4xl text-charcoal mb-6">{t.thankYou}</h1>
            <p className="text-text-secondary mb-8">
              {t.thankYouMessage}
            </p>
            <button 
              onClick={() => setSubmitStatus(null)}
              className="btn-gold"
            >
              {t.sendAnother}
            </button>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <Header />
      
      {/* Hero */}
      <section className="pt-12 pb-8 bg-cream">
        <div className="container mx-auto px-6 max-w-5xl text-center">
          <AnimatedSection animation="fadeUp">
            <p className="eyebrow mb-6">{t.title}</p>
            <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-6">
              {t.heroTitle}
            </h1>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t.heroSubtitle}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Contact Grid */}
      <section className="py-16 bg-cream">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left - Info */}
            <AnimatedSection animation="fadeRight">
              <div className="mb-12">
                <h2 className="font-serif text-2xl text-charcoal mb-6">
                  {t.clinicName}
                </h2>
                <p className="text-text-muted text-sm mb-8">
                  Centro Sanitario NICA · Marbella
                </p>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 border border-gold flex items-center justify-center flex-shrink-0">
                      <Clock className="text-gold" size={16} />
                    </div>
                    <div>
                      <p className="text-xs tracking-wider uppercase text-text-muted mb-1">{t.schedule}</p>
                      <p className="text-charcoal">{t.scheduleValue}</p>
                      <p className="text-text-secondary">{t.saturday}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 border border-gold flex items-center justify-center flex-shrink-0">
                      <Globe className="text-gold" size={16} />
                    </div>
                    <div>
                      <p className="text-xs tracking-wider uppercase text-text-muted mb-1">{t.languages}</p>
                      <p className="text-charcoal">{t.languagesValue}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 border border-gold flex items-center justify-center flex-shrink-0">
                      <Phone className="text-gold" size={16} />
                    </div>
                    <div>
                      <p className="text-xs tracking-wider uppercase text-text-muted mb-1">{t.phone}</p>
                      <a href="tel:+34952764547" className="text-gold hover:text-gold/70 transition-colors">
                        +34 952 76 45 47
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 border border-gold flex items-center justify-center flex-shrink-0">
                      <Mail className="text-gold" size={16} />
                    </div>
                    <div>
                      <p className="text-xs tracking-wider uppercase text-text-muted mb-1">{t.email}</p>
                      <a href="mailto:info@drangelnorro.com" className="text-gold hover:text-gold/70 transition-colors">
                        info@drangelnorro.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 border border-gold flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="text-gold" size={16} />
                    </div>
                    <div>
                      <p className="text-xs tracking-wider uppercase text-text-muted mb-1">{t.whatsapp}</p>
                      <a 
                        href="https://wa.me/34952764547" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gold hover:text-gold/70 transition-colors"
                      >
                        {t.sendMessage}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 border border-gold flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-gold" size={16} />
                    </div>
                    <div>
                      <p className="text-xs tracking-wider uppercase text-text-muted mb-1">{t.address}</p>
                      <p className="text-charcoal">C/ Carlos Mackintosh, 3, 5º, 3ª</p>
                      <p className="text-text-secondary">29602 Marbella, Málaga</p>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* Right - Form */}
            <AnimatedSection animation="fadeLeft">
              <div className="bg-ivory p-8 md:p-10 border border-divider">
                <h3 className="font-serif text-2xl text-charcoal mb-8">
                  {t.formTitle}
                </h3>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-xs tracking-wider uppercase text-text-muted mb-2">
                      {t.name} *
                    </label>
                    <input
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      required
                      className="input-luxury w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-xs tracking-wider uppercase text-text-muted mb-2">
                      {t.emailField} *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="input-luxury w-full"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-text-muted mb-2">
                        {t.phoneField} / WhatsApp
                      </label>
                      <input
                        type="tel"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        className="input-luxury w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-text-muted mb-2">
                        {t.country}
                      </label>
                      <input
                        type="text"
                        name="pais"
                        value={formData.pais}
                        onChange={handleChange}
                        className="input-luxury w-full"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs tracking-wider uppercase text-text-muted mb-2">
                      {t.city}
                    </label>
                    <input
                      type="text"
                      name="ciudad"
                      value={formData.ciudad}
                      onChange={handleChange}
                      className="input-luxury w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-xs tracking-wider uppercase text-text-muted mb-2">
                      {t.procedure}
                    </label>
                    <select
                      name="procedimiento"
                      value={formData.procedimiento}
                      onChange={handleChange}
                      className="input-luxury w-full bg-ivory"
                    >
                      <option value="">---</option>
                      {currentProcedimientos.map((proc, index) => (
                        <option 
                          key={index} 
                          value={proc}
                          disabled={proc.startsWith('—')}
                        >
                          {proc}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs tracking-wider uppercase text-text-muted mb-2">
                      {t.referredBy}
                    </label>
                    <select
                      name="referidoPor"
                      value={formData.referidoPor}
                      onChange={handleChange}
                      className="input-luxury w-full bg-ivory"
                    >
                      <option value="">---</option>
                      {(t.referredByOptions || []).map((opt, index) => (
                        <option key={index} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-text-muted mb-2">
                        {t.preferredContact}
                      </label>
                      <select
                        name="medioPreferido"
                        value={formData.medioPreferido}
                        onChange={handleChange}
                        className="input-luxury w-full bg-ivory"
                      >
                        <option value="">---</option>
                        {(t.preferredContactOptions || []).map((opt, index) => (
                          <option key={index} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs tracking-wider uppercase text-text-muted mb-2">
                        {t.contactSchedule}
                      </label>
                      <select
                        name="horarioContacto"
                        value={formData.horarioContacto}
                        onChange={handleChange}
                        className="input-luxury w-full bg-ivory"
                      >
                        <option value="">---</option>
                        {(t.contactScheduleOptions || []).map((opt, index) => (
                          <option key={index} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs tracking-wider uppercase text-text-muted mb-2">
                      {t.message}
                    </label>
                    <textarea
                      name="mensaje"
                      value={formData.mensaje}
                      onChange={handleChange}
                      rows={4}
                      className="input-luxury w-full resize-none"
                    />
                  </div>

                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      name="privacidad"
                      checked={formData.privacidad}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                    <label className="text-text-muted text-sm">
                      {t.privacy}
                    </label>
                  </div>

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
                      Error. Please try again or call us directly.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting || !formData.privacidad}
                    className="btn-gold w-full flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        {t.sending}
                      </>
                    ) : (
                      t.send
                    )}
                  </button>
                </form>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="h-[400px] relative">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1597.5!2d-4.8853192!3d36.508194!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd7327f8604a4f9b%3A0x134493038ea2c9d0!2sC.%20Carlos%20Mackintosh%2C%203%2C%2029602%20Marbella%2C%20M%C3%A1laga!5e0!3m2!1ses!2ses!4v1700000000000!5m2!1ses!2ses"
          width="100%"
          height="100%"
          style={{ border: 0, filter: 'grayscale(30%)' }}
          allowFullScreen=""
          loading="lazy"
          title="Location Dr. Ángel Norro"
        ></iframe>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Contacto;
